import { PaystackSuccess, SuccessPaymentArgs } from 'TYPES/api.body';
import { NewUseUpdatedCart } from 'components/molecules/Cart/hooks';
import QUERY_KEYS from 'network/config/queryKeys';
import { getAllAddress } from 'network/services/address';
import { submitOrder } from 'network/services/order';
import { getCustomer } from 'network/services/profile';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { cartListAtom } from 'store/atoms/cartAtom';
import { DEFAULT_CHECKOUT_DETAILS, checkoutDetailsAtom } from 'store/atoms/checkoutAtom';
import checkoutVariablesAtom from 'store/atoms/checkoutVariables';
import modalAtom from 'store/atoms/modals';
import { userRoleAtom } from 'store/atoms/userAtom';
import { v4 as uuidv4 } from 'uuid';

export default function usePayment() {
  const { orderId } = useParams();
  const [user, checkNewUser] = useState('');
  const serverCart = NewUseUpdatedCart()?.data;
  const cartOrder = (serverCart?.orders as any[])?.find(order => order?.locationId === Number(orderId));
  const subTotal = cartOrder?.totalAmount || 0;
  const submissionInProgress = useRef(false);
  const processedPaymentRefs = useRef(new Set());

  useEffect(() => {
    return checkNewUser(localStorage.getItem('newuser') ?? 'false');
  }, []);
  const go = useNavigate();
  const queryClient = useQueryClient();
  const userRole = useRecoilValue(userRoleAtom);
  const { data: addressData, isLoading: addressLoading } = useQuery({
    queryFn: getAllAddress,
    queryKey: QUERY_KEYS.CUSTOMER_ADDRESSES
  });
  const defaultAddress = React.useMemo(() => {
    const addresses = addressData?.data ? addressData.data : [];
    const defAddr = addresses.find(item => item.status === true);
    if (!defAddr && addresses.length > 0) {
      return addresses[0];
    }
    return defAddr;
  }, [addressData]);

  const setModalState = useSetRecoilState(modalAtom);
  const setCartList = useSetRecoilState(cartListAtom);
  const [checkoutDetails, setCheckoutDetails] = useRecoilState(checkoutDetailsAtom);
  const [checkoutVariables, setCheckoutVariables] = useRecoilState(checkoutVariablesAtom);
  const { mutateAsync: _submitOrder, isLoading: isSubmitOrderLoading } = useMutation({ mutationFn: submitOrder });
  const { data: customerProfile } = useQuery({
    queryFn: getCustomer,
    queryKey: QUERY_KEYS.CUSTOMER_PROFILE
  });

  const handleOrderSuccess = async (orderId?: string) => {
    enqueueSnackbar('Order Placed Successfully', {
      variant: 'success'
    });
    localStorage.removeItem('cart');
    setCartList({});
    setCheckoutVariables(curr => ({
      ...curr,
      checkoutLoading: isSubmitOrderLoading
    }));
    setCheckoutVariables(curr => ({
      ...curr,
      pointsUsed: {
        points: 0,
        pointNairaEquivalent: 0
      }
    }));
    go('/account/orders', { replace: true });
    setCheckoutDetails(DEFAULT_CHECKOUT_DETAILS);
    setCheckoutVariables(curr => ({
      ...curr,
      deliveryOccurrence: 'None'
    }));
    queryClient.invalidateQueries([QUERY_KEYS.CART]);
    queryClient.invalidateQueries([QUERY_KEYS.ALL_ORDERS]);
    queryClient.invalidateQueries([QUERY_KEYS.COMPLETE_ORDERS]);
    queryClient.invalidateQueries([QUERY_KEYS.ONGOING_ORDERS]);
    queryClient.invalidateQueries([QUERY_KEYS.CANCELED_ORDERS]);
    setModalState(curr => ({
      ...curr,
      paymentModalOpen: false,
      paymentSuccessModalOpen: true,
      paymentFailureModalOpen: false,
      transactionInProgressModalOpen: false
    }));
  };

  const handleOrderFailure = (error: Error) => {
    enqueueSnackbar(`Something went wrong: ${error.message}`, {
      variant: 'error'
    });
    setModalState(curr => ({
      ...curr,
      paymentModalOpen: false,
      paymentSuccessModalOpen: false,
      paymentFailureModalOpen: true,
      transactionInProgressModalOpen: false
    }));
    setCheckoutVariables(curr => ({
      ...curr,
      pointsUsed: {
        points: 0,
        pointNairaEquivalent: 0
      }
    }));
  };

  const initiateBusinessPostPayment = () => {
    if (checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier === undefined) {
      enqueueSnackbar('Please select a carrier', { variant: 'error' });
      return;
    }

    setModalState(c => ({ ...c, transactionInProgressModalOpen: true }));

    let grandTotal = subTotal + (checkoutDetails.shippingFee ?? 0) - checkoutVariables.pointsUsed.pointNairaEquivalent;

    _submitOrder({
      ...checkoutDetails,
      orderId: cartOrder?.id,
      locationId: cartOrder?.locationId,
      addressId: defaultAddress?.id,
      has10PercentDiscount: user === 'true' ? true : false,
      deliveryAgentId: checkoutVariables.selectedCarrier?.carrierId
        ? parseInt(checkoutVariables.selectedCarrier.carrierId)
        : undefined,
      logistic:
        checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier !== undefined
          ? {
            carrierId: checkoutVariables.selectedCarrier.courier_id,
            serviceCode: checkoutVariables.selectedCarrier.service_code,
            carrierName: checkoutVariables.selectedCarrier.courier_name,
            carrierLogo: checkoutVariables.selectedCarrier.courier_logo,
            amount: checkoutVariables.selectedCarrier.total,
            estimatedDaysOfDelivery: checkoutVariables.selectedCarrier.delivery_eta
            // eta: checkoutVariables.selectedCarrier.delivery_eta_time
          }
          : null,
      points: checkoutVariables.pointsUsed.points,
      customerId: customerProfile?.data.id,
      grandTotal,
      total: grandTotal,
      subAmount: user === 'true' ? subTotal - subTotal * 0.1 : subTotal,
      driverNote: checkoutDetails.driverNote && checkoutDetails.driverNote !== '' ? checkoutDetails.driverNote : 'null',
      paymentType: 'Post Payment',
      paymentTrxRef: uuidv4(),
      paymentMode: 'post_payment',
      customerType: userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR',
      request_token: checkoutDetails.request_token
    })
      .then((data: any) => {
        console.log(data);
        if (data.data.paymentStatus === 'Success' || data.data.paymentStatus === 'success') {
          handleOrderSuccess(data.data.id);
        } else {
          handleOrderFailure(new Error('Payment not successful'));
        }
      })
      .catch((err: any) => handleOrderFailure(err));
  };

  const handlePaymentSuccess = (args: SuccessPaymentArgs) => {
    if (args.reference && processedPaymentRefs.current.has(args.reference)) {
      console.warn('Payment reference already processed:', args.reference);
      return;
    }

    if (submissionInProgress.current) {
      console.warn('Order submission already in progress');
      return;
    }

    // Mark as processed
    if (args.reference) {
      processedPaymentRefs.current.add(args.reference);
    }
    submissionInProgress.current = true;

    _submitOrder({
      ...checkoutDetails,
      request_token: checkoutVariables?.request_token || '',
      driverNote: checkoutVariables.driverNote,
      logistic:
        checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier !== undefined
          ? {
            carrierId: checkoutVariables.selectedCarrier.courier_id,
            serviceCode: checkoutVariables.selectedCarrier.service_code,
            carrierName: checkoutVariables.selectedCarrier.courier_name,
            carrierLogo: checkoutVariables.selectedCarrier.courier_logo,
            amount: checkoutVariables.selectedCarrier.total,
            estimatedDaysOfDelivery: checkoutVariables.selectedCarrier.delivery_eta,
            request_token: checkoutVariables.request_token
            // eta: checkoutVariables.selectedCarrier.delivery_eta_time
          }
          : null,
      has10PercentDiscount: user === 'true' ? true : false,
      customerType: userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR',
      recurringDelivery: !(checkoutVariables.deliveryOccurrence === 'None'),
      recurringDeliveryEndDate:
        checkoutVariables.deliveryOccurrence === 'None' ? undefined : checkoutDetails.recurringDeliveryEndDate,
      deliverySchedule: checkoutDetails.deliveryMethod === 'pickup' ? 'Immediately' : checkoutDetails.deliverySchedule,
      status: args.status,
      paymentTrxRef: args.reference ?? uuidv4(),
      paymentMode: args.paymentMode,
      paymentType: args.paymentType,
      paymentStatus: args.status as any,
      orderId: cartOrder?.id,
      locationId: cartOrder?.locationId,
      addressId: defaultAddress?.id
    })
      .then((data: any) => {
        if (data.data.paymentStatus === 'SUCCESS' || data.data.paymentStatus === 'Success') {
          handleOrderSuccess(data.data.id);
        } else {
          handleOrderFailure(new Error('Payment not successful'));
        }
      })
      .catch((err: any) => {
        if (args.reference) {
          processedPaymentRefs.current.delete(args.reference);
        }
        submissionInProgress.current = false;
        handleOrderFailure(err);
      })
      .finally(() => {
        submissionInProgress.current = false;
      });
  };
  const onWalletSuccess = () => {
    setModalState(c => ({
      ...c,
      transactionInProgressModalOpen: true,
      paymentModalOpen: false
    }));
    handlePaymentSuccess({
      status: 'Success',
      paymentMode: 'Wallet',
      paymentType: 'Madina Wallet'
    });
  };

  const onSuccess = (reference?: PaystackSuccess) => {
    setModalState(c => ({
      ...c,
      transactionInProgressModalOpen: true,
      paymentModalOpen: false
    }));
    if (reference !== undefined) {
      handlePaymentSuccess({
        paymentMode: 'PayStack',
        reference: reference.reference,
        status: reference.status,
        paymentType: 'Credit Card'
      });
    } else {
      handleOrderFailure(new Error('No reference found'));
    }
  };

  const onClose = () => {
    setModalState(curr => ({
      ...curr,
      paymentModalOpen: false,
      paymentSuccessModalOpen: false,
      paymentFailureModalOpen: false,
      transactionInProgressModalOpen: false
    }));
  };

  const initiatePayment = () => {
    if (checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier === undefined) {
      enqueueSnackbar('Please select a carrier', { variant: 'error' });
      return;
    }

    let grandTotal = subTotal + (checkoutDetails.shippingFee ?? 0) - checkoutVariables.pointsUsed.pointNairaEquivalent;
    setCheckoutDetails(curr => ({ ...curr, grandTotal }));
    setCheckoutDetails(prev => ({
      ...prev,
      request_token: checkoutVariables.request_token || '',
      deliveryAgentId: checkoutVariables.selectedCarrier?.courier_id
        ? parseInt(checkoutVariables.selectedCarrier.courier_id)
        : undefined,
      logistic:
        checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier !== undefined
          ? {
            carrierId: checkoutVariables.selectedCarrier.courier_id,
            serviceCode: checkoutVariables.selectedCarrier.service_code,
            carrierName: checkoutVariables.selectedCarrier.courier_name,
            carrierLogo: checkoutVariables.selectedCarrier.courier_logo,
            amount: checkoutVariables.selectedCarrier.total,
            estimatedDaysOfDelivery: checkoutVariables.selectedCarrier.delivery_eta,
            request_token: checkoutVariables.request_token
            // eta: checkoutVariables.selectedCarrier.delivery_eta_time
          }
          : null,
      points: checkoutVariables.pointsUsed.points,
      customerId: customerProfile?.data.id,
      grandTotal,
      total: grandTotal,
      subAmount: user === 'true' ? subTotal - subTotal * 0.1 : subTotal,
      orderId: cartOrder?.id,
      locationId: cartOrder?.locationId,
      addressId: defaultAddress?.id
    }));
    setModalState(curr => ({ ...curr, paymentModalOpen: true }));
  };

  return {
    onClose,
    onSuccess,
    initiatePayment,
    onWalletSuccess,
    handleOrderSuccess,
    handleOrderFailure,
    initiateBusinessPostPayment
  };
}
